// src/features/MyGymTV/UK/AddDS/AddDS.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Button, Alert, Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Layout from '../../../../layout/Layout';
import UKDatePicker from '../../UK/UKDatePicker/UKDatePicker';
import './add-ds.scss';
import axios from 'axios';
import moment from 'moment';


const AddD6 = () => {
    const { selectedClubs } = useSelector((state) => state.ukEntities);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        imageFile: null,
        imagePreview: null,
        filename: '',
        startDate: null,
        endDate: null
    });

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                setErrors({ ...errors, image: 'Please upload a valid image file' });
                return;
            }

            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                const width = img.width;
                const height = img.height;

                if (width === 1080 && height === 1920) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                        setFormData({
                            ...formData,
                            imageFile: file,
                            imagePreview: reader.result,
                            filename: formData.filename || file.name
                        });
                        setErrors({ ...errors, image: null });
                    };
                    reader.readAsDataURL(file);
                } else {
                    setFormData({
                        ...formData,
                        imageFile: null,
                        imagePreview: null
                    });
                    setErrors({
                        ...errors,
                        image: `Invalid image dimensions. D6 content must be exactly 1080x1920 pixels (portrait). Your image is ${width}x${height}.`
                    });
                }

                URL.revokeObjectURL(objectUrl);
            };

            img.onerror = () => {
                setErrors({ ...errors, image: 'Failed to load image. Please try a different file.' });
                URL.revokeObjectURL(objectUrl);
            };

            img.src = objectUrl;
        }
    };

    const handleDeleteImage = () => {
        setFormData({
            ...formData,
            imageFile: null,
            imagePreview: null
        });
    };

    const handleInputChange = (field, value) => {
        setFormData({ ...formData, [field]: value });
        if (errors[field]) {
            setErrors({ ...errors, [field]: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.imageFile) {
            newErrors.image = 'Please upload an image';
        }
        if (!formData.filename.trim()) {
            newErrors.filename = 'Filename is required';
        }
        if (!formData.startDate) {
            newErrors.startDate = 'Start date is required';
        }
        if (!formData.endDate) {
            newErrors.endDate = 'End date is required';
        }
        if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
            newErrors.endDate = 'End date must be after start date';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePublishClick = () => {
        if (validateForm()) {
            setShowConfirmModal(true);
        }
    };

    const handleConfirmPublish = async () => {
        setIsSubmitting(true);

        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();
            formDataToSend.append('image', formData.imageFile);
            formDataToSend.append('filename', formData.filename);
            formDataToSend.append('startDate', moment(formData.startDate).format('YYYY-MM-DD'));
            formDataToSend.append('endDate', moment(formData.endDate).format('YYYY-MM-DD'));

            const clubData = selectedClubs.map(c => ({ ClubId: c.ClubId, ClubName: c.ClubName }));
            formDataToSend.append('clubIds', JSON.stringify(clubData));

            const response = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/api/UK/AddD6Content`,
                formDataToSend,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    }
                }
            );

            setShowConfirmModal(false);
            resetForm();

            alert(`Content published successfully to ${response.data.SuccessfulClubs} venues!`);
            navigate('/uk/review-d6');

        } catch (error) {
            console.error('Error publishing content:', error);
            setErrors({ submit: error.response?.data || 'Failed to publish content. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            imageFile: null,
            imagePreview: null,
            filename: '',
            startDate: null,
            endDate: null
        });
        setErrors({});
    };

    if (selectedClubs.length === 0) {
        return (
            <Layout>
                <div className="add-d6-container mt-5">
                    <Alert variant="warning">
                        Please select one or more venues from the menu to add content.
                    </Alert>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="add-d6-container mt-5">
                <Card>
                    <Card.Header>
                        <h4>Add D6 Content</h4>
                        <small className="text-muted">
                            Adding content for {selectedClubs.length} selected venue(s)
                        </small>
                    </Card.Header>
                    <Card.Body>
                        {errors.submit && (
                            <Alert
                                dismissible
                                onClose={() => setErrors({ ...errors, submit: null })}
                                variant="danger"
                            >
                                {errors.submit}
                            </Alert>
                        )}

                        <Form>
                            {/* Image Upload */}
                            <Form.Group className="mb-4">
                                <Form.Label>Upload Image (1080x1920 portrait) *</Form.Label>
                                {!formData.imagePreview ? (
                                    <>
                                        <Form.Control
                                            accept="image/*"
                                            isInvalid={!!errors.image}
                                            onChange={handleImageUpload}
                                            type="file"
                                        />
                                        <Form.Control.Feedback type="invalid">
                                            {errors.image}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            D6 content must be exactly 1080x1920 pixels in portrait orientation
                                        </Form.Text>
                                    </>
                                ) : (
                                    <div className="image-preview-container">
                                        <img
                                            alt="Preview"
                                            className="img-thumbnail mb-2"
                                            src={formData.imagePreview}
                                            style={{ maxWidth: '300px', maxHeight: '300px' }}
                                        />
                                        <div className="mb-2">
                                            <span className="text-success">✓ Valid D6 image (1080x1920)</span>
                                        </div>
                                        <div>
                                            <Button
                                                onClick={handleDeleteImage}
                                                size="sm"
                                                variant="danger"
                                            >
                                                Delete Image
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </Form.Group>

                            {/* Filename */}
                            <Form.Group className="mb-3">
                                <Form.Label>Filename *</Form.Label>
                                <Form.Control
                                    isInvalid={!!errors.filename}
                                    onChange={(e) => handleInputChange('filename', e.target.value)}
                                    placeholder="Enter filename"
                                    type="text"
                                    value={formData.filename}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.filename}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {/* Date Range */}
                            <div className="row">
                                <div className="col-md-6">
                                    <UKDatePicker
                                        error={errors.startDate}
                                        label="Start Date *"
                                        minDate={new Date()}
                                        onChange={(date) => handleInputChange('startDate', date)}
                                        selected={formData.startDate}
                                    />
                                </div>
                                <div className="col-md-6">
                                    <UKDatePicker
                                        error={errors.endDate}
                                        label="End Date *"
                                        minDate={formData.startDate || new Date()}
                                        onChange={(date) => handleInputChange('endDate', date)}
                                        selected={formData.endDate}
                                    />
                                </div>
                            </div>

                            {/* Submit Button */}
                            <div className="d-flex justify-content-end gap-2">
                                <Button
                                    disabled={isSubmitting}
                                    onClick={resetForm}
                                    variant="secondary"
                                >
                                    Reset
                                </Button>
                                <Button
                                    disabled={isSubmitting || !formData.imageFile || !!errors.image}
                                    onClick={handlePublishClick}
                                    variant="primary"
                                >
                                    Publish
                                </Button>
                            </div>
                        </Form>
                    </Card.Body>
                </Card>

                {/* Confirmation Modal */}
                <Modal
                    onHide={() => setShowConfirmModal(false)}
                    show={showConfirmModal}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>Confirm Publication</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <p><strong>Filename:</strong> {formData.filename}</p>
                        <p><strong>When:</strong> {formData.startDate && moment(formData.startDate).format('DD/MM/YYYY')} to {formData.endDate && moment(formData.endDate).format('DD/MM/YYYY')}</p>
                        <p><strong>Where:</strong></p>
                        <ul>
                            {selectedClubs.map(club => (
                                <li key={club.ClubId}>{club.ClubName}</li>
                            ))}
                        </ul>
                        <p>Are you sure you want to publish this content?</p>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button
                            disabled={isSubmitting}
                            onClick={() => setShowConfirmModal(false)}
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                        <Button
                            disabled={isSubmitting}
                            onClick={handleConfirmPublish}
                            variant="primary"
                        >
                            {isSubmitting ? 'Publishing...' : 'Confirm'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            </div>
        </Layout>
    );
};

export default AddD6;